// Buscar › «Archivos» (shared/API.md §41.5): team-wide file search. The server answers (the store
// keeps no file index), so the list says «Buscando…» until it lands; a result shows the picture or
// an extension tile, the name, size, the conversation and the date; clicking opens the file (the
// viewer for pictures, the browser's viewer for PDFs, a download otherwise) and «Ir al mensaje»
// jumps to the message that carried it.
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { data } from '../../../bridge/client';
import { AttachmentKind } from '../../../core/attachmentKind';
import { RelativeTime } from '../../../core/relativeTime';
import { extensionBadge } from '../../../core/teamFileSearch';
import type { TeamFileRow } from '../../../data/features/teamFiles';
import { Icon } from '../../../design/icons';
import { t } from '../../../i18n/i18n';
import { useMinuteClock } from '../../hooks';
import { Button, EmptyState, InlineStatus } from '../../kit/Kit';
import { mediaUrls } from '../../media';
import { useShell } from '../../shell/ShellContext';
import { openLightbox } from '../attachments/Lightbox';
import { openPdf } from '../attachments/PostFiles';
import { timeZoneOffsetSeconds } from './searchModel';

const PAUSE_MS = 450;

function isImage(f: TeamFileRow): boolean {
  return AttachmentKind.classify(f.mimeType, f.name, f.width, f.height) === 'image';
}

function opensInline(f: TeamFileRow): boolean {
  return isImage(f) || f.mimeType === 'application/pdf';
}

type State = { query: string; rows: TeamFileRow[]; page: number; hasMore: boolean; loading: boolean; failed: boolean };

export function FileSearchResults({ teamId, query }: { teamId: string; query: string }) {
  const q = query.trim();
  const [state, setState] = useState<State>({ query: '', rows: [], page: 0, hasMore: false, loading: false, failed: false });
  const seq = useRef(0);
  const stateRef = useRef(state);
  stateRef.current = state;

  const load = useCallback(
    (terms: string, page: number) => {
      const mine = ++seq.current;
      const seen = page === 0 ? [] : stateRef.current.rows.map((r) => r.fileId);
      setState((s) => ({ ...s, query: terms, loading: true, failed: false, ...(page === 0 ? { rows: [], hasMore: false } : {}) }));
      data.call('searchTeamFiles', teamId, terms, page, timeZoneOffsetSeconds(), seen).then(
        (r) => {
          if (mine !== seq.current) return; // a newer query owns the list
          if ('error' in r) return setState((s) => ({ ...s, loading: false, failed: true }));
          setState((s) => ({ query: terms, rows: page === 0 ? r.rows : [...s.rows, ...r.rows], page, hasMore: r.hasMore, loading: false, failed: false }));
        },
        () => mine === seq.current && setState((s) => ({ ...s, loading: false, failed: true })),
      );
    },
    [teamId],
  );

  // Ask after a pause in typing (the server is the only index here).
  useEffect(() => {
    if (!q) {
      seq.current++;
      setState({ query: '', rows: [], page: 0, hasMore: false, loading: false, failed: false });
      return;
    }
    const id = setTimeout(() => load(q, 0), PAUSE_MS);
    return () => clearTimeout(id);
  }, [q, load]);

  const sentinel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !state.hasMore || state.loading) return;
    const io = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && load(stateRef.current.query, stateRef.current.page + 1));
    io.observe(el);
    return () => io.disconnect();
  }, [state.hasMore, state.loading, state.rows.length, load]);

  if (!q) return <EmptyState title={t('web.search.scope.files')} body={t('web.search.files.start')} />;
  if (state.failed && !state.rows.length) return <InlineStatus kind="error" text={t('web.search.files.failed')} onRetry={() => load(q, 0)} />;
  if (!state.rows.length) {
    return state.loading || state.query !== q ? <InlineStatus kind="loading" text={t('web.search.searchingServer')} /> : <EmptyState title={t('web.search.files.empty')} />;
  }
  return (
    <>
      <FileList rows={state.rows} busy={state.loading} />
      {state.failed ? <InlineStatus kind="error" text={t('web.search.files.failed')} onRetry={() => load(state.query, state.page + 1)} /> : null}
      {state.hasMore ? <div ref={sentinel} className="search-more" /> : null}
      {state.loading ? <InlineStatus kind="loading" text={t('web.search.searchingServer')} /> : null}
    </>
  );
}

const FileList = memo(function FileList({ rows, busy }: { rows: TeamFileRow[]; busy: boolean }) {
  const shell = useShell();
  const now = useMinuteClock();
  const images = rows.filter(isImage);
  return (
    <div className="search-results sf-files" role="list" aria-busy={busy}>
      {rows.map((f) => {
        const caption = [AttachmentKind.sizeLabel(f.size), f.channelName, RelativeTime.format(f.createAt, now, { yesterday: t('channelList.time.yesterday') })].filter(Boolean).join(' · ');
        return (
          <div role="listitem" key={f.fileId} className="sf-file">
            <a
              className="sf-file-main"
              href={mediaUrls.file(f.fileId, !opensInline(f))}
              target="_blank"
              rel="noopener noreferrer"
              title={t('web.search.files.open')}
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0 || !opensInline(f)) return;
                e.preventDefault();
                if (!isImage(f)) return openPdf(f.fileId, f.name);
                openLightbox(
                  images.map((x) => ({ fileId: x.fileId, name: x.name, author: x.username ? '@' + x.username : '', at: x.createAt, width: x.width, height: x.height, mimeType: x.mimeType })),
                  images.indexOf(f),
                );
              }}
            >
              {isImage(f) ? (
                <span className="sf-thumb">
                  <img src={mediaUrls.thumbnail(f.fileId)} alt="" loading="lazy" decoding="async" />
                </span>
              ) : (
                <span className="sf-thumb sf-thumb-doc" aria-hidden>
                  {extensionBadge(f.name, f.extension)}
                </span>
              )}
              <span className="sf-file-text">
                <span className="sf-file-name">{f.name}</span>
                <span className="sf-file-caption">
                  {f.channelType === 'P' ? <Icon name="lock" size={11} /> : null}
                  {caption}
                </span>
              </span>
            </a>
            {f.postId ? (
              <Button kind="quiet" onClick={() => shell.jumpToPost(f.postId)}>
                {t('web.search.files.goToMessage')}
              </Button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
});
